// @ts-nocheck
import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar } from 'lucide-react';
import { DayPicker } from 'react-day-picker';

import { formatIsoYmdForDisplay, localDateToIsoYmd, parseLocalDateFromIsoYmd } from '../../../utils/isoDateDisplay';

import 'react-day-picker/style.css';
import './WorkflowDateField.css';

const PANEL_W = 300;
const PANEL_H = 380;

/** Popover calendar for workflow forms (month/year dropdowns; portal avoids table overflow clip). */
export function WorkflowDateField({
  value,
  onChange,
  disabled = false,
  className = '',
  buttonClassName = '',
  variant = 'default',
  label,
  id: idProp,
  compact = false,
}) {
  const autoId = useId();
  const id = idProp ?? `wf-date-${autoId}`;
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const popRef = useRef(null);

  const iso = String(value ?? '').trim();
  const selected = parseLocalDateFromIsoYmd(iso);
  const displayLabel = selected ? formatIsoYmdForDisplay(iso) : 'Select date…';

  const variantBtn =
    variant === 'amber'
      ? 'border-amber-400 ring-1 ring-amber-200 focus:ring-amber-500 focus:border-amber-500'
      : 'border-gray-300 focus:ring-emerald-600 focus:border-emerald-600';

  const sizeClass = compact ? 'h-9 min-h-[2.25rem] text-xs px-2' : 'h-10 min-h-[2.5rem] text-xs px-3';

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = r.left;
    if (left + PANEL_W > vw - 8) left = Math.max(8, vw - PANEL_W - 8);
    let top = r.bottom + 6;
    if (top + PANEL_H > vh - 8) top = Math.max(8, r.top - PANEL_H - 6);
    setCoords({ top, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      const t = e.target;
      if (btnRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const year = new Date().getFullYear();

  const picker =
    open &&
    createPortal(
      <div
        ref={popRef}
        className="fixed z-[10050] rounded-lg border border-gray-200 bg-white p-0 shadow-xl"
        style={{ top: coords.top, left: coords.left }}
        role="dialog"
        aria-label="Choose date"
      >
        <DayPicker
          mode="single"
          selected={selected ?? undefined}
          onSelect={(d) => {
            if (d) {
              onChange(localDateToIsoYmd(d));
              setOpen(false);
            }
          }}
          defaultMonth={selected ?? new Date()}
          captionLayout="dropdown"
          fromYear={year - 80}
          toYear={year + 15}
          disabled={disabled}
          classNames={{
            root: 'workflow-day-picker-rdp rdp-root rounded-lg !p-2 !text-sm',
          }}
        />
      </div>,
      document.body
    );

  const control = (
    <div className={`relative inline-block w-full ${className}`}>
      <button
        ref={btnRef}
        type="button"
        id={id}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => !disabled && setOpen((o) => !o)}
        className={[
          'inline-flex w-full min-w-[9rem] items-center justify-between gap-2 rounded-md border bg-white text-left font-normal text-gray-900 shadow-sm outline-none transition focus:ring-2',
          sizeClass,
          variantBtn,
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:border-gray-400',
          !selected && 'text-gray-500',
          buttonClassName,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <span className="min-w-0 truncate">{displayLabel}</span>
        <Calendar className="h-4 w-4 shrink-0 text-emerald-700/90" aria-hidden />
      </button>
      {picker}
    </div>
  );

  if (label) {
    return (
      <div>
        <label htmlFor={id} className="mb-2 block text-[15px] font-medium text-gray-700">
          {label}
        </label>
        {control}
      </div>
    );
  }

  return control;
}
